import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, CheckIcon, ArrowDownTrayIcon, CheckCircleIcon, PrinterIcon } from '@heroicons/react/24/outline';
import DownloadEmailModal from '../DownloadEmailModal';
import PrintOrderModal from './PrintOrderModal';
import { MatureCover, MATURE_BLUR, useMatureConsent } from './MatureCover';
import { withGalleryPass } from '../../lib/galleryPass';

interface Photo {
    id: string;
    title: string;
    thumbnail_url: string;
    google_drive_file_id: string;
    mime_type?: string | null;
    is_mature?: boolean | null;
    metadata?: { width?: number; height?: number } | null;
}

interface PhotoLightboxProps {
    photo: Photo | null;
    onClose: () => void;
    onNext: () => void;
    onPrev: () => void;
    isSelected: boolean;
    isPurchased?: boolean;
    // A zero-priced gallery: every photo is downloadable without a purchase.
    // Unlocks the same way a purchase does, minus the PROPRIETARY badge.
    isFree?: boolean;
    onToggleSelect: () => void;
    singlePhotoPrice?: number;
    galleryName?: string;
}

const PhotoLightbox: React.FC<PhotoLightboxProps> = ({
    photo,
    onClose,
    onNext,
    onPrev,
    isSelected,
    isPurchased = false,
    isFree = false,
    onToggleSelect,
    singlePhotoPrice = 5.00,
    galleryName
}) => {
    const [isImageLoading, setIsImageLoading] = useState(true);
    const isUnlocked = isPurchased || isFree;
    const [matureOk, confirmMature] = useMatureConsent();
    const hideMature = Boolean(photo?.is_mature) && !matureOk;
    const [downloadEmailOpen, setDownloadEmailOpen] = useState(false);
    const [printModalOpen, setPrintModalOpen] = useState(false);

    const triggerDownload = (email: string) => {
        const fileId = photo.google_drive_file_id || photo.id;
        const url = withGalleryPass(`/api/gallery/stream?fileId=${fileId}&download=true&email=${encodeURIComponent(email)}`);
        window.open(url, '_blank');
    };

    const handleDownloadClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const cached = typeof window !== 'undefined'
            ? (localStorage.getItem('tlau_download_email') || localStorage.getItem('credit_email'))
            : null;
        if (cached && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cached)) {
            triggerDownload(cached);
            return;
        }
        setDownloadEmailOpen(true);
    };

    // Reset loading state when photo changes
    useEffect(() => {
        setIsImageLoading(true);
    }, [photo?.google_drive_file_id]);

    if (!photo) return null;


    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
                onContextMenu={(e) => e.preventDefault()}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/95 backdrop-blur-xl"
                    onClick={onClose}
                />

                {/* Content Container */}
                <div className="relative w-full max-w-6xl h-full flex flex-col items-center justify-center group">

                    {/* Header: Gallery Name */}
                    <motion.div
                        initial={{ y: -10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="mb-8 md:mb-12 mt-8"
                    >
                        <h3 className="text-white text-[10px] md:text-[11px] font-black tracking-[0.4em] md:tracking-[0.5em] uppercase">
                            {galleryName || 'Archive View'}
                        </h3>
                    </motion.div>

                    {/* Main Image Container */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="flex flex-col items-center justify-center w-full h-full pointer-events-none p-4"
                    >
                        {/* Inner generic wrapper that shrinks to fit the image */}
                        <div className="relative w-fit h-auto flex items-center justify-center overflow-hidden">
                            {hideMature && (
                                <div className="pointer-events-auto">
                                    <MatureCover onConfirm={confirmMature} />
                                </div>
                            )}

                            {/* Brand Overlay (preview protection) - Only if not purchased and image is loaded */}
                            {!isUnlocked && (
                                <div className={`absolute inset-0 z-10 flex items-center justify-center pointer-events-none overflow-hidden transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}>
                                    <img
                                        src="/logo.png"
                                        alt=""
                                        className="w-1/2 h-auto brightness-0 invert opacity-[0.09] select-none"
                                        style={{
                                            WebkitTouchCallout: 'none',
                                            WebkitUserSelect: 'none',
                                            userSelect: 'none'
                                        }}
                                    />
                                </div>
                            )}

                            {/* Protection Layer - Intercepts all touch/click events when not purchased */}
                            {!isUnlocked && (
                                <div
                                    className={`absolute inset-0 z-20 transition-opacity duration-300 ${isImageLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                                    onContextMenu={(e) => e.preventDefault()}
                                    onTouchStart={(e) => e.preventDefault()}
                                    style={{
                                        WebkitTouchCallout: 'none',
                                        WebkitUserSelect: 'none',
                                        userSelect: 'none'
                                    }}
                                />
                            )}

                            {photo.mime_type?.startsWith('video/') ? (
                                // onLoadedMetadata, not onLoad: a <video> never fires
                                // onLoad, so reusing the image handler would leave the
                                // player stuck behind the loading veil forever.
                                <video
                                    src={`/api/gallery/stream?fileId=${photo.google_drive_file_id}`}
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="metadata"
                                    className={`max-h-[70vh] w-auto object-contain shadow-2xl pointer-events-auto ${hideMature ? MATURE_BLUR : ''}`}
                                    onLoadedMetadata={() => setIsImageLoading(false)}
                                    onContextMenu={(e) => e.preventDefault()}
                                />
                            ) : (
                                <img
                                    src={`/api/gallery/stream?fileId=${photo.google_drive_file_id}&size=3000`}
                                    alt={photo.title}
                                    className={`max-h-[70vh] w-auto object-contain shadow-2xl select-none transition-all duration-700 ${!isUnlocked ? 'pointer-events-none' : 'pointer-events-auto'} ${hideMature ? MATURE_BLUR : ''}`}
                                    onContextMenu={(e) => e.preventDefault()}
                                    draggable={false}
                                    onLoad={() => setIsImageLoading(false)}
                                    style={{
                                        WebkitTouchCallout: 'none',
                                        WebkitUserSelect: 'none',
                                        userSelect: 'none'
                                    }}
                                />
                            )}

                            {/* Close Button - Overlaid on Photo - Only show when loaded */}
                            <button
                                onClick={onClose}
                                className={`absolute -top-12 right-0 md:top-4 md:right-4 z-50 p-2 bg-black/40 hover:bg-white backdrop-blur-md rounded-full text-white hover:text-black transition-all pointer-events-auto ${isImageLoading ? 'opacity-0 scale-0' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 scale-100'}`}
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>

                            {/* Status Badge - Only show when loaded */}
                            {isPurchased && (
                                <div className={`absolute top-4 left-4 z-20 flex items-center gap-1.5 bg-white text-black px-2 py-1 rounded-none text-[8px] md:text-[9px] font-black uppercase tracking-[0.15em] shadow-lg transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}>
                                    <CheckCircleIcon className="w-3 h-3" />
                                    PROPRIETARY
                                </div>
                            )}
                        </div>
                    </motion.div>

                    <button
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className={`absolute left-0 p-4 text-white hover:text-zinc-400 transition-all ${isImageLoading ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
                    >
                        <ChevronLeftIcon className="w-12 h-12" />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className={`absolute right-0 p-4 text-white hover:text-zinc-400 transition-all ${isImageLoading ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'}`}
                    >
                        <ChevronRightIcon className="w-12 h-12" />
                    </button>

                    <motion.div
                        initial={{ y: 0, opacity: 0 }}
                        animate={{ y: 0, opacity: isImageLoading ? 0 : 1 }}
                        className="mt-2 flex flex-col items-center gap-3 transition-opacity duration-300"
                    >
                        <div className="flex flex-wrap items-center justify-center gap-3">
                        {isUnlocked ? (
                            <button
                                onClick={handleDownloadClick}
                                className="flex items-center gap-2 px-8 py-3 bg-white hover:bg-zinc-200 text-black rounded-none font-black text-[10px] uppercase tracking-[0.25em] transition-all shadow-xl"
                            >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                <span>Download Original</span>
                            </button>
                        ) : (
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
                                className={`flex items-center gap-2 px-10 py-4 rounded-none font-black text-xs uppercase tracking-widest transition-all ${isSelected
                                    ? 'bg-zinc-800 text-white'
                                    : 'bg-white text-black hover:bg-zinc-200'
                                    }`}
                            >
                                {isSelected ? (
                                    <>
                                        <CheckIcon className="w-5 h-5 text-zinc-900" />
                                        <span>Selected</span>
                                    </>
                                ) : (
                                    <>
                                        <PlusIcon className="w-5 h-5" />
                                        <span>Add to Selection ({singlePhotoPrice === 0 ? 'Free' : `$${singlePhotoPrice.toFixed(2)}`})</span>
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); setPrintModalOpen(true); }}
                            className="flex items-center gap-2 px-6 py-3 md:px-10 md:py-4 rounded-none font-black text-xs uppercase tracking-widest transition-all bg-transparent text-white border border-white hover:bg-white hover:text-black"
                        >
                            <PrinterIcon className="w-4 h-4" />
                            <span>Order Print</span>
                        </button>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
            {printModalOpen && (
                <PrintOrderModal
                    photo={{
                        id: photo.id,
                        title: photo.title,
                        googleDriveFileId: photo.google_drive_file_id,
                        metadata: photo.metadata,
                    }}
                    onClose={() => setPrintModalOpen(false)}
                />
            )}
            <DownloadEmailModal
                isOpen={downloadEmailOpen}
                onClose={() => setDownloadEmailOpen(false)}
                onSubmit={(email) => {
                    localStorage.setItem('tlau_download_email', email);
                    localStorage.setItem('credit_email', email);
                    setDownloadEmailOpen(false);
                    triggerDownload(email);
                }}
                photoCount={1}
            />
        </AnimatePresence>
    );
};

export default PhotoLightbox;
